/* @img-migrated */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Settings,
  Type,
  Calendar,
  Newspaper,
  Users,
  Building2,
  Megaphone,
  MousePointerClick,
  Image as ImageIcon,
  Minus,
  LayoutPanelTop,
  MessageSquare,
  Gift,
  Award,
  X,
  Check,
  Monitor,
  Smartphone,
  RefreshCw,
  UserCircle2,
} from 'lucide-react';
import { BlogImageUploader } from './BlogImageUploader';

import Img from './Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCK_CATALOG = [
  { type: 'header', label: 'Header', icon: LayoutPanelTop, description: 'Logo + title + subtitle', hasAuto: false },
  { type: 'intro', label: 'Intro Text', icon: Type, description: 'Opening paragraph', hasAuto: false },
  { type: 'president_message', label: 'President Message', icon: MessageSquare, description: 'Quote-styled message', hasAuto: true },
  { type: 'leadership_message', label: 'Leadership Message', icon: UserCircle2, description: 'Formal letter from a leader', hasAuto: false },
  { type: 'announcement', label: 'Announcement', icon: Megaphone, description: 'Highlighted callout', hasAuto: false },
  { type: 'events', label: 'Events', icon: Calendar, description: 'Upcoming events cards', hasAuto: true },
  { type: 'blogs', label: 'Blogs / News', icon: Newspaper, description: 'Latest articles', hasAuto: true },
  { type: 'featured_members', label: 'Featured Members', icon: Users, description: '2×2 member grid', hasAuto: true },
  { type: 'business_spotlight', label: 'Business Spotlight', icon: Building2, description: 'Featured businesses', hasAuto: true },
  { type: 'deals', label: 'Member Deals', icon: Gift, description: 'Discount offers', hasAuto: true },
  { type: 'leadership', label: 'Leadership', icon: Award, description: 'Board members', hasAuto: true },
  { type: 'cta', label: 'CTA Button', icon: MousePointerClick, description: 'Call-to-action panel', hasAuto: false },
  { type: 'image', label: 'Image', icon: ImageIcon, description: 'Full-width image', hasAuto: false },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Line / dots / space', hasAuto: false },
  { type: 'footer', label: 'Footer', icon: LayoutPanelTop, description: 'Contact + social', hasAuto: false },
];

const BLOCK_META = Object.fromEntries(BLOCK_CATALOG.map((b) => [b.type, b]));

const defaultBlockData = (type) => {
  switch (type) {
    case 'header': return { type, title: 'DCCF Newsletter', subtitle: '' };
    case 'intro': return { type, body: 'Welcome to this edition of our newsletter.', align: 'left' };
    case 'president_message': return { type, mode: 'auto', title: '', body: '', author: '' };
    case 'leadership_message': return { type, title: 'Leadership Message', leader_id: '', body: '' };
    case 'announcement': return { type, title: 'Important Update', body: 'Replace with your announcement text.' };
    case 'events': return { type, mode: 'auto', title: 'Upcoming Events', limit: 4, source_ids: [] };
    case 'blogs': return { type, mode: 'auto', title: 'Latest Stories', limit: 5, source_ids: [] };
    case 'featured_members': return { type, mode: 'auto', title: 'Featured Members', limit: 4, source_ids: [] };
    case 'business_spotlight': return { type, mode: 'auto', title: 'Business Spotlight', limit: 3, source_ids: [] };
    case 'deals': return { type, mode: 'auto', title: 'Member Deals', limit: 5, source_ids: [] };
    case 'leadership': return { type, mode: 'auto', title: 'Leadership', limit: 3, source_ids: [] };
    case 'cta': return { type, title: 'Stay Connected', body: 'Join the conversation.', cta_text: 'Open Portal', cta_url: '' };
    case 'image': return { type, src: '', caption: '' };
    case 'divider': return { type, style: 'line' };
    case 'footer': return { type };
    default: return { type };
  }
};

const uid = () => `b_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

/**
 * VisualBlockEditor
 * Props:
 *  - value: { template, accent_color, subject, banner_url, blocks: [] }
 *  - onChange(nextValue)
 *  - disabled: boolean
 */
const VisualBlockEditor = ({ value, onChange, disabled = false }) => {
  const blocks = value?.blocks || [];
  const [selectedId, setSelectedId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerInsertIndex, setPickerInsertIndex] = useState(blocks.length);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewViewport, setPreviewViewport] = useState('desktop');
  const previewTimer = useRef(null);

  // Ensure every block has a stable _uid for React keys + selection tracking
  useEffect(() => {
    if (blocks.length > 0 && blocks.some((b) => !b._uid)) {
      onChange({ ...value, blocks: blocks.map((b) => (b._uid ? b : { ...b, _uid: uid() })) });
    }
     
  }, [blocks.length]);

  const refreshPreview = useCallback(async () => {
    setPreviewBusy(true);
    try {
      const payload = {
        template: value.template || 'community',
        accent_color: value.accent_color,
        subject: value.subject || '(no subject)',
        banner_url: value.banner_url,
        // strip _uid before sending
        blocks: (value.blocks || []).map(({ _uid: _, ...rest }) => rest),
      };
      const res = await axios.post(`${API}/admin/newsletters/render-preview`, payload, { withCredentials: true });
      setPreviewHtml(res.data.html);
    } catch (err) {
      // silent — keep last preview
    } finally {
      setPreviewBusy(false);
    }
  }, [value]);

  // Debounced live preview
  useEffect(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => refreshPreview(), 420);
    return () => { if (previewTimer.current) clearTimeout(previewTimer.current); };
  }, [value.blocks, value.template, value.accent_color, value.subject, value.banner_url, refreshPreview]);

  const updateBlock = (idOrIndex, patch) => {
    const next = blocks.map((b) => (b._uid === idOrIndex ? { ...b, ...patch } : b));
    onChange({ ...value, blocks: next });
  };

  const deleteBlock = (id) => {
    onChange({ ...value, blocks: blocks.filter((b) => b._uid !== id) });
    if (selectedId === id) setSelectedId(null);
  };

  const moveBlock = (id, direction) => {
    const idx = blocks.findIndex((b) => b._uid === id);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange({ ...value, blocks: next });
  };

  const addBlock = (type) => {
    const nb = { ...defaultBlockData(type), _uid: uid() };
    const idx = pickerInsertIndex;
    const next = [...blocks];
    next.splice(idx, 0, nb);
    onChange({ ...value, blocks: next });
    setPickerOpen(false);
    setSelectedId(nb._uid);
  };

  const loadTemplatePreset = async (templateId) => {
    try {
      const res = await axios.get(`${API}/admin/newsletters/template-preset/${templateId}`, { withCredentials: true });
      const stamped = (res.data.blocks || []).map((b) => ({ ...b, _uid: uid() }));
      onChange({
        ...value,
        template: res.data.template,
        accent_color: res.data.accent_color,
        blocks: stamped,
      });
      toast.success('Template applied — customize each block or send as-is.');
    } catch {
      toast.error('Failed to load template preset');
    }
  };

  const selected = blocks.find((b) => b._uid === selectedId);

  return (
    <div className="grid xl:grid-cols-[minmax(0,1fr)_minmax(400px,0.9fr)] gap-5" data-testid="visual-block-editor">
      {/* ===== BUILDER CANVAS ===== */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-[11.5px] font-semibold uppercase tracking-wide text-slate-500">
            Blocks ({blocks.length})
          </p>
          <button
            type="button"
            onClick={() => { setPickerInsertIndex(blocks.length); setPickerOpen(true); }}
            disabled={disabled}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12px] font-semibold bg-[#002249] text-white hover:bg-[#001a38] disabled:opacity-40 u-btn"
            data-testid="vbe-add-block-top"
          >
            <Plus className="w-3.5 h-3.5" /> Add Block
          </button>
        </div>

        {blocks.length === 0 && (
          <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center" data-testid="vbe-empty">
            <p className="text-[14px] font-semibold text-slate-600">No blocks yet</p>
            <p className="text-[12.5px] text-slate-400 mt-1 mb-4">Pick a template or add blocks one by one.</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {['executive', 'community', 'events', 'business'].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => loadTemplatePreset(t)}
                  className="px-3 h-8 rounded-md border border-slate-200 bg-white hover:border-[#002249] text-[12px] font-semibold text-[#002249] u-btn capitalize"
                  data-testid={`vbe-preset-${t}`}
                >
                  Load {t}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {blocks.map((b, i) => {
            const meta = BLOCK_META[b.type] || { label: b.type, icon: Settings };
            const Icon = meta.icon;
            const isSelected = selectedId === b._uid;
            return (
              <motion.div
                layout
                key={b._uid}
                className={`rounded-lg border bg-white overflow-hidden transition-all ${
                  isSelected ? 'border-[#002249] ring-2 ring-[#002249]/15' : 'border-slate-200 hover:border-slate-300'
                }`}
                data-testid={`vbe-block-${i}`}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(isSelected ? null : b._uid)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedId(isSelected ? null : b._uid); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left cursor-pointer"
                  data-testid={`vbe-block-${i}-toggle`}
                >
                  <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-slate-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-[#002249] truncate">{meta.label}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {b.title || b.body?.slice(0, 50) || (b.mode === 'auto' ? 'Auto — pulled from system' : meta.description)}
                      {b.mode === 'auto' && <span className="ml-1.5 inline-block px-1.5 py-0 rounded text-[9.5px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">AUTO</span>}
                      {b.mode === 'manual' && <span className="ml-1.5 inline-block px-1.5 py-0 rounded text-[9.5px] font-bold bg-amber-100 text-amber-800 border border-amber-200">MANUAL</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                    <button type="button" onClick={() => moveBlock(b._uid, 'up')} disabled={disabled || i === 0} className="w-7 h-7 rounded hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center" data-testid={`vbe-block-${i}-up`}><ArrowUp className="w-3.5 h-3.5 text-slate-500" /></button>
                    <button type="button" onClick={() => moveBlock(b._uid, 'down')} disabled={disabled || i === blocks.length - 1} className="w-7 h-7 rounded hover:bg-slate-100 disabled:opacity-30 flex items-center justify-center" data-testid={`vbe-block-${i}-down`}><ArrowDown className="w-3.5 h-3.5 text-slate-500" /></button>
                    <button type="button" onClick={() => deleteBlock(b._uid)} disabled={disabled} className="w-7 h-7 rounded hover:bg-red-50 text-red-500 disabled:opacity-30 flex items-center justify-center" data-testid={`vbe-block-${i}-delete`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>

                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-slate-100 bg-slate-50/60 px-4 py-3"
                    >
                      <BlockEditor
                        block={b}
                        onChange={(patch) => updateBlock(b._uid, patch)}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {blocks.length > 0 && (
          <button
            type="button"
            onClick={() => { setPickerInsertIndex(blocks.length); setPickerOpen(true); }}
            disabled={disabled}
            className="w-full h-10 rounded-lg border-2 border-dashed border-slate-200 hover:border-[#002249]/50 text-[12.5px] font-semibold text-slate-500 hover:text-[#002249] flex items-center justify-center gap-2 u-btn"
            data-testid="vbe-add-block-bottom"
          >
            <Plus className="w-4 h-4" /> Add Block
          </button>
        )}
      </div>

      {/* ===== LIVE PREVIEW ===== */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden h-[calc(100vh-220px)] min-h-[480px] flex flex-col" data-testid="vbe-preview">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
          <div className="min-w-0">
            <h3 className="text-[13px] font-bold text-[#002249] flex items-center gap-1.5">
              Live Preview
              {previewBusy && <RefreshCw className="w-3 h-3 animate-spin text-slate-400" />}
            </h3>
            <p className="text-[11px] text-slate-500">Updates instantly as you edit</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPreviewViewport('desktop')}
              className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-[11px] font-semibold transition-colors ${previewViewport === 'desktop' ? 'bg-[#002249] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="vbe-preview-desktop"
            >
              <Monitor className="w-3 h-3" /> Desktop
            </button>
            <button
              type="button"
              onClick={() => setPreviewViewport('mobile')}
              className={`inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-[11px] font-semibold transition-colors ${previewViewport === 'mobile' ? 'bg-[#002249] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              data-testid="vbe-preview-mobile"
            >
              <Smartphone className="w-3 h-3" /> Mobile
            </button>
          </div>
        </div>
        <div className="bg-slate-100 flex-1 overflow-y-auto flex justify-center p-3">
          {previewHtml ? (
            <iframe
              title="preview"
              srcDoc={previewHtml}
              className={`bg-white rounded-md border border-slate-200 shadow-sm transition-all duration-200 ${previewViewport === 'mobile' ? 'w-[390px]' : 'w-full'} h-full`}
              sandbox=""
            />
          ) : (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm">Add a block to see the preview.</div>
          )}
        </div>
      </div>

      {/* ===== BLOCK PICKER MODAL ===== */}
      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={() => setPickerOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', damping: 28, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full sm:max-w-[700px] sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden"
              data-testid="vbe-block-picker"
            >
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-[15px] font-bold text-[#002249]">Add a Block</h3>
                  <p className="text-[11px] text-slate-500">Pick any block to insert — you can edit it after.</p>
                </div>
                <button onClick={() => setPickerOpen(false)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
                {BLOCK_CATALOG.map((b) => {
                  const Icon = b.icon;
                  return (
                    <button
                      key={b.type}
                      type="button"
                      onClick={() => addBlock(b.type)}
                      className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#002249] hover:bg-[#002249]/5 text-left u-btn"
                      data-testid={`vbe-pick-${b.type}`}
                    >
                      <div className="w-8 h-8 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-[#002249] flex items-center gap-1.5">
                          {b.label}
                          {b.hasAuto && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1">AUTO</span>}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{b.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ---------- Per-block editor panel ---------- */
const BlockEditor = ({ block, onChange }) => {
  const input = "w-full px-3 h-9 text-[13px] rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]/40";
  const textarea = "w-full px-3 py-2 text-[13px] rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20 focus:border-[#002249]/40 resize-none";
  const label = "text-[10.5px] font-semibold uppercase tracking-wide text-slate-500 block mb-1";

  const ModeSwitch = () => (
    <div className="mb-2 inline-flex rounded-md border border-slate-200 p-0.5 bg-white">
      {[['auto', 'Auto'], ['manual', 'Manual']].map(([mv, ml]) => (
        <button
          key={mv}
          type="button"
          onClick={() => onChange({ mode: mv })}
          className={`px-2.5 h-7 rounded text-[11px] font-semibold transition-colors ${
            (block.mode || 'auto') === mv ? 'bg-[#002249] text-white' : 'text-slate-600 hover:bg-slate-50'
          }`}
          data-testid={`vbe-mode-${block.type}-${mv}`}
        >{ml}</button>
      ))}
    </div>
  );

  const TitleField = () => (
    <div>
      <label className={label}>Section Title</label>
      <input className={input} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} placeholder="Optional heading" />
    </div>
  );

  switch (block.type) {
    case 'header':
      return (
        <div className="space-y-2.5">
          <div><label className={label}>Title</label><input className={input} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></div>
          <div><label className={label}>Subtitle</label><input className={input} value={block.subtitle || ''} onChange={(e) => onChange({ subtitle: e.target.value })} placeholder="Optional one-liner below the title" /></div>
          <div>
            <label className={label}>Custom Logo (optional — leave empty for DCCF default)</label>
            <BlogImageUploader
              value={block.logo_url || ''}
              position={'center'}
              onChange={(url) => onChange({ logo_url: url })}
              label=""
              aspect="wide"
              hint="PNG / SVG-as-PNG · transparent background recommended"
            />
          </div>
        </div>
      );
    case 'intro':
      return (
        <div className="space-y-2.5">
          <div>
            <label className={label}>Body Text</label>
            <textarea rows={3} className={textarea} value={block.body || ''} onChange={(e) => onChange({ body: e.target.value })} placeholder="Intro paragraph — you can use <strong>bold</strong>." />
          </div>
          <div>
            <label className={label}>Alignment</label>
            <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-white">
              {['left', 'center', 'right'].map((a) => (
                <button key={a} type="button" onClick={() => onChange({ align: a })} className={`px-2.5 h-7 rounded text-[11px] font-semibold capitalize ${(block.align || 'left') === a ? 'bg-[#002249] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{a}</button>
              ))}
            </div>
          </div>
        </div>
      );
    case 'announcement':
      return (
        <div className="space-y-2.5">
          <div><label className={label}>Title</label><input className={input} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></div>
          <div><label className={label}>Body</label><textarea rows={2} className={textarea} value={block.body || ''} onChange={(e) => onChange({ body: e.target.value })} /></div>
        </div>
      );
    case 'cta':
      return (
        <div className="space-y-2.5">
          <div><label className={label}>Title</label><input className={input} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></div>
          <div><label className={label}>Body</label><input className={input} value={block.body || ''} onChange={(e) => onChange({ body: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className={label}>Button Text</label><input className={input} value={block.cta_text || ''} onChange={(e) => onChange({ cta_text: e.target.value })} /></div>
            <div><label className={label}>Button URL</label><input className={input} value={block.cta_url || ''} onChange={(e) => onChange({ cta_url: e.target.value })} placeholder="https://..." /></div>
          </div>
        </div>
      );
    case 'image':
      return (
        <div className="space-y-2.5">
          <BlogImageUploader
            value={block.src || ''}
            position={block.position || 'center'}
            onChange={(url, pos) => onChange({ src: url, position: pos })}
            label="Image"
            hint="Recommended 16:9 · PNG / JPG / WebP · 8 MB max"
          />
          <div><label className={label}>Caption (optional)</label><input className={input} value={block.caption || ''} onChange={(e) => onChange({ caption: e.target.value })} /></div>
        </div>
      );
    case 'divider':
      return (
        <div>
          <label className={label}>Style</label>
          <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-white">
            {[['line', 'Line'], ['dots', 'Dots'], ['space', 'Space']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => onChange({ style: v })} className={`px-2.5 h-7 rounded text-[11px] font-semibold ${(block.style || 'line') === v ? 'bg-[#002249] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{l}</button>
            ))}
          </div>
        </div>
      );
    case 'footer':
      return (
        <div className="space-y-2.5">
          <div><label className={label}>Address</label><input className={input} value={block.address || ''} onChange={(e) => onChange({ address: e.target.value })} placeholder="28 Broadway Ave, Suite 209, Kissimmee, FL 34741" /></div>
          <div><label className={label}>Contact Email</label><input className={input} value={block.contact || ''} onChange={(e) => onChange({ contact: e.target.value })} /></div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className={label}>Facebook</label><input className={input} value={block.social?.facebook || ''} onChange={(e) => onChange({ social: { ...(block.social || {}), facebook: e.target.value } })} /></div>
            <div><label className={label}>Instagram</label><input className={input} value={block.social?.instagram || ''} onChange={(e) => onChange({ social: { ...(block.social || {}), instagram: e.target.value } })} /></div>
            <div><label className={label}>LinkedIn</label><input className={input} value={block.social?.linkedin || ''} onChange={(e) => onChange({ social: { ...(block.social || {}), linkedin: e.target.value } })} /></div>
          </div>
        </div>
      );
    case 'president_message':
      return (
        <div className="space-y-2.5">
          <ModeSwitch />
          {block.mode === 'auto'
            ? <p className="text-[11.5px] text-slate-500">Auto mode: pulls the latest President Message from platform settings. Save and switch to Manual if you want to override.</p>
            : <>
                <div><label className={label}>Title</label><input className={input} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} /></div>
                <div><label className={label}>Message</label><textarea rows={3} className={textarea} value={block.body || ''} onChange={(e) => onChange({ body: e.target.value })} /></div>
                <div><label className={label}>Author</label><input className={input} value={block.author || ''} onChange={(e) => onChange({ author: e.target.value })} /></div>
              </>}
        </div>
      );
    case 'leadership_message':
      return <LeadershipMessageEditor block={block} onChange={onChange} label={label} input={input} textarea={textarea} />;
    case 'events':
    case 'blogs':
    case 'featured_members':
    case 'business_spotlight':
    case 'deals':
    case 'leadership':
      return (
        <div className="space-y-2.5">
          <ModeSwitch />
          <TitleField />
          {(block.mode || 'auto') === 'auto' ? (
            <div className="flex items-center gap-2">
              <label className={`${label} mb-0 flex-shrink-0`}>Show up to</label>
              <input type="number" min={1} max={10} className="w-16 px-2 h-8 text-[12px] rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#002249]/20" value={block.limit || 4} onChange={(e) => onChange({ limit: parseInt(e.target.value) || 4 })} />
              <span className="text-[11.5px] text-slate-500">items · auto-pulled from platform</span>
            </div>
          ) : (
            <ManualContentPicker block={block} onChange={onChange} />
          )}
        </div>
      );
    default:
      return <p className="text-[12px] text-slate-500">No editable fields for this block.</p>;
  }
};

/* ---------- Leadership Message — leader picker + letter-body editor ---------- */
const LeadershipMessageEditor = ({ block, onChange, label, input, textarea }) => {
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/leadership`);
        setLeaders(res.data || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const selected = leaders.find((l) => l.id === block.leader_id);

  return (
    <div className="space-y-3" data-testid="leadership-message-editor">
      <div>
        <label className={label}>Section Label (optional)</label>
        <input className={input} value={block.title || ''} onChange={(e) => onChange({ title: e.target.value })} placeholder="Leadership Message" />
      </div>

      <div>
        <label className={label}>Select Leader</label>
        <select
          className={input}
          value={block.leader_id || ''}
          onChange={(e) => onChange({ leader_id: e.target.value })}
          data-testid="leadership-message-select"
        >
          <option value="">{loading ? 'Loading leaders…' : '— Pick a leader —'}</option>
          {leaders.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name} — {l.title}
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="leadership-message-preview-card">
          {selected.image_url ? (
            <Img variant="generic"
              src={resolveImg(selected.image_url)}
              alt={selected.name}
              className="w-14 h-14 rounded-full object-cover border-2 border-[#002249]"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-[#002249] text-white flex items-center justify-center text-[15px] font-bold">
              {(selected.name || '').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[13px] font-bold text-[#002249] truncate">{selected.name}</p>
            <p className="text-[11.5px] text-slate-500 truncate">{selected.title}</p>
            {selected.bio && <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{selected.bio}</p>}
          </div>
        </div>
      )}

      <div>
        <label className={label}>
          Message Body
          <span className="text-[10.5px] font-normal text-slate-400 ml-1">
            — plain text. Press Enter twice for a new paragraph.
          </span>
        </label>
        <textarea
          rows={10}
          className={textarea}
          value={block.body || ''}
          onChange={(e) => onChange({ body: e.target.value })}
          placeholder={'Dear Members,\n\nIt is my honor to share with you...\n\nWith warm regards,'}
          data-testid="leadership-message-body"
        />
      </div>

      {!block.leader_id && (
        <p className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Pick a leader above to auto-load their photo, name, and title into the email.
        </p>
      )}
    </div>
  );
};


/* ---------- Manual content picker (for events/blogs/members/businesses/deals) ---------- */
const ManualContentPicker = ({ block, onChange }) => {
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/newsletters/content-pool?days=90`, { withCredentials: true });
        setPool(res.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const sourceMap = {
    events: (pool?.upcoming_events || []).concat(pool?.past_events || []),
    blogs: pool?.blogs || [],
    featured_members: pool?.featured_members || [],
    business_spotlight: pool?.featured_businesses || [],
    deals: pool?.deals || [],
    leadership: pool?.leadership || [],
  };
  const items = sourceMap[block.type] || [];
  const idKey = block.type === 'business_spotlight' || block.type === 'deals' ? 'user_id' : 'id';

  const selectedIds = block.source_ids || [];
  const toggle = (id) => {
    const has = selectedIds.includes(id);
    onChange({ source_ids: has ? selectedIds.filter((s) => s !== id) : [...selectedIds, id] });
  };

  if (loading) return <p className="text-[11.5px] text-slate-400">Loading content…</p>;
  if (items.length === 0) return <p className="text-[11.5px] text-slate-400">No items available for this block type. Switch back to Auto.</p>;

  return (
    <div className="space-y-1.5 max-h-[220px] overflow-y-auto border border-slate-200 rounded-md p-2 bg-white">
      {items.map((it) => {
        const id = it[idKey];
        const label = it.title || it.name || it.company_name || id;
        const selected = selectedIds.includes(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => toggle(id)}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-[12.5px] ${selected ? 'bg-[#002249]/5 border border-[#002249]/30' : 'hover:bg-slate-50 border border-transparent'}`}
            data-testid={`vbe-manual-item-${id}`}
          >
            <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? 'bg-[#002249] border-[#002249]' : 'border-slate-300'}`}>
              {selected && <Check className="w-3 h-3 text-white" />}
            </span>
            <span className="truncate">{label}</span>
          </button>
        );
      })}
      <p className="text-[10.5px] text-slate-400 px-1">Selected: {selectedIds.length}</p>
    </div>
  );
};

export default VisualBlockEditor;
